// Brand grid + tabs filter (homepage).
//
// One row of mutually-exclusive quick-filter chips above the grid, spanning
// two filter dimensions but only ONE active at a time:
//   1. Condition: Toate | Cu parcurs | Noi             (param: mode)
//   2. Fuel:      Diesel | Plugin | Hybrid | Benzină   (param: fuel)
//
// Clicking any chip selects ONLY that filter and clears every other chip.
// "Toate" resets to no filter. The two dimensions never combine.
//
// All brand data + counts come from WordPress (AP_DATA.brands localized by the
// theme, or REST /brand-counts when filters change). No bundled mock data.
import { qs, qsa, on, delegate } from '../lib/dom.js';
import { AP_DATA } from '../data/listings.js';
import { t, sprintf } from '../lib/i18n.js';

const STATE = {
  condition: 'all',  // 'all' | 'used' | 'new'
  fuel: '',          // '' | 'Diesel' | 'PHEV' | 'Hibrid' | 'Benzină'
};

const CONDITION_LABELS = {
  get all() { return t('cond_all', 'Toate'); },
  get used() { return t('cond_used', 'Cu parcurs'); },
  get new() { return t('cond_new', 'Noi'); },
};
const FUEL_LABELS = { 'plug-in-hybrid': 'Plugin-Hybrid', hybrid: 'Hybrid', diesel: 'Diesel', benzina: 'Benzină' };
let expanded = false;
let cached = null; // last fetched brand list

// Collapsed-view tile limit per breakpoint = rows × columns: 4 rows on
// desktop/tablet, 8 rows on the 2-col mobile layout. Mirrors the CSS truncation
// in brand-tile.css; used here only to decide whether to show the toggle.
function currentLimit() {
  const w = window.innerWidth;
  if (w >= 1025) return 24;  // 6 cols × 4 rows
  if (w >= 769) return 16;  // 4 cols × 4 rows
  if (w >= 481) return 12;  // 3 cols × 4 rows
  return 16;                 // 2 cols × 8 rows
}

function activeFiltersLabel() {
  const parts = [];
  if (STATE.condition !== 'all') parts.push(CONDITION_LABELS[STATE.condition]);
  if (STATE.fuel) parts.push(FUEL_LABELS[STATE.fuel] || STATE.fuel);
  return parts;
}

function catalogUrl() {
  return AP_DATA.catalog || '/cars/';
}

function brandHref(brandName) {
  const params = new URLSearchParams();
  params.set('brand', brandName);
  if (STATE.condition !== 'all') params.set('mode', STATE.condition);
  if (STATE.fuel) params.set('fuel', STATE.fuel);
  return `${catalogUrl()}?${params.toString()}`;
}

function tileHTML(b) {
  return `
    <a class="ap-brand-tile" href="${brandHref(b.name)}">
      <span class="ap-brand-tile__logo" aria-hidden="true">
        ${b.logo
      ? `<img src="${b.logo}" alt="" loading="lazy" decoding="async" onerror="this.parentElement.classList.add('is-missing'); this.remove();">`
      : ''}
        <span class="ap-brand-tile__logo-fallback">${(b.name || '?').charAt(0)}</span>
      </span>
      <span class="ap-brand-tile__name">
        ${b.name}${b.isNew ? `<span class="ap-brand-tile__badge">${t('brand_new_badge', 'Nou')}</span>` : ''}
      </span>
      <span class="ap-brand-tile__count">${b.count}</span>
    </a>
  `;
}

// Fetch brand counts for the current (condition, fuel) state from REST.
async function fetchBrands() {
  // No filters → use the WP-localized list (server already pre-computed counts).
  if (STATE.condition === 'all' && !STATE.fuel) {
    return (AP_DATA.brands || []).filter((b) => (b.count || 0) > 0);
  }
  if (!AP_DATA.apiBase) return AP_DATA.brands || [];

  const params = new URLSearchParams();
  if (STATE.condition !== 'all') params.set('mode', STATE.condition);
  if (STATE.fuel) params.set('fuel', STATE.fuel);
  try {
    const res = await fetch(`${AP_DATA.apiBase}brand-counts?${params}`, {
      headers: AP_DATA.nonce ? { 'X-WP-Nonce': AP_DATA.nonce } : {},
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error('REST');
    return await res.json();
  } catch {
    return AP_DATA.brands || [];
  }
}

export function initBrandGrid() {
  const grid = qs('[data-role="brand-grid"]');
  const tabs = qs('[data-component="brand-tabs"]');
  const toggle = qs('[data-action="toggle-brands"]');
  const toggleLabel = qs('[data-role="brand-toggle-label"]');
  if (!grid) return;

  // Server-rendered first paint - leave the existing markup alone until the
  // user actually changes a filter.
  const serverRendered = grid.dataset.serverRendered === '1';
  if (serverRendered) delete grid.dataset.serverRendered;

  // Monotonic token: a newer tab click abandons any in-flight render, so a
  // slow response can neither clear the spinner early nor paint stale tiles.
  let loadSeq = 0;

  async function render({ skipFetch = false } = {}) {
    let list = cached;
    if (!skipFetch || !list) {
      const seq = ++loadSeq;
      // Loading feedback while the counts round-trip: dim the current tiles
      // and show the spinner (brand-tile.css .is-loading).
      grid.classList.add('is-loading');
      list = await fetchBrands();
      if (seq !== loadSeq) return;
      grid.classList.remove('is-loading');
      cached = list;
    }

    if (!list || list.length === 0) {
      const filters = activeFiltersLabel();
      const subtitle = filters.length
        ? sprintf(t('no_cars_filtered', 'Nu avem momentan mașini %s în stoc.'), filters.map((f) => `<strong>${f}</strong>`).join(' + '))
        : t('no_cars_available', 'Nu avem momentan mașini disponibile.');
      grid.innerHTML = `
        <div class="ap-brand-grid__empty">
          <span class="ap-brand-grid__empty-icon" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
          </span>
          <h3 class="ap-brand-grid__empty-title">${t('no_results_title', 'Niciun rezultat')}</h3>
          <p class="ap-brand-grid__empty-body">${subtitle} ${t('no_results_hint', 'Încearcă să schimbi filtrele sau te anunțăm noi când apare ceva potrivit.')}</p>
          <div class="ap-brand-grid__empty-actions">
            <button class="ap-btn ap-btn--dark ap-btn--sm" type="button" data-action="reset-brand-filters">${t('reset_filters', 'Resetează filtrele')}</button>
            <a class="ap-btn ap-btn--ghost ap-btn--sm" href="${catalogUrl()}">${t('view_catalog', 'Vezi tot catalogul')}</a>
          </div>
        </div>`;
      if (toggle) toggle.hidden = true;
      return;
    }

    // Render the FULL list - CSS (brand-tile.css) truncates it to N rows while
    // the grid is collapsed; the toggle reveals the rest via `.is-expanded`.
    // Most-stocked brands first (alphabetical within equal counts) - matches
    // the server-rendered order in brand-grid.php.
    list = list.slice().sort((a, b) =>
      (b.count || 0) - (a.count || 0) || String(a.name).localeCompare(String(b.name)));
    grid.innerHTML = list.map(tileHTML).join('');
    grid.classList.toggle('is-expanded', expanded);
    updateToggle(list.length);
  }

  function updateToggle(count) {
    if (!toggle) return;
    const overflow = count > currentLimit();
    toggle.hidden = !overflow;
    toggle.classList.toggle('is-expanded', expanded);
    if (toggleLabel) toggleLabel.textContent = expanded ? t('show_fewer', 'Mai puține') : t('all_brands', 'Toate mărcile');
  }

  // Only render on first paint when the server didn't.
  if (!serverRendered) {
    render();
  } else {
    // Cache the initial set so subsequent renders are fast even from the
    // no-filter state, and set the toggle from the server-rendered tile count.
    cached = (AP_DATA.brands || []).filter((b) => (b.count || 0) > 0);
    updateToggle(grid.children.length);
  }

  if (toggle) {
    on(toggle, 'click', () => {
      expanded = !expanded;
      grid.classList.toggle('is-expanded', expanded);
      updateToggle(grid.children.length);
    });
  }
  // Breakpoint changes alter the collapsed row-limit → re-check toggle visibility.
  on(window, 'resize', () => { updateToggle(grid.children.length); });

  delegate(grid, 'click', '[data-action="reset-brand-filters"]', () => {
    STATE.condition = 'all';
    STATE.fuel = '';
    if (tabs) {
      qsa('[data-condition]', tabs).forEach((t) => t.classList.toggle('is-active', t.dataset.condition === 'all'));
      qsa('[data-fuel]', tabs).forEach((t) => t.classList.remove('is-active'));
    }
    cached = null;
    render();
  });

  if (!tabs) return;

  // Single-select across BOTH dimensions: only one chip is ever active. The two
  // chip groups (condition + fuel) are mutually exclusive - picking a fuel chip
  // clears any condition selection and vice versa, so the catalog link carries
  // exactly one filter (or none, for "Toate").
  const deactivateAll = () => {
    qsa('.ap-chip', tabs).forEach((t) => t.classList.remove('is-active'));
  };

  delegate(tabs, 'click', '[data-condition]', (e, btn) => {
    deactivateAll();
    btn.classList.add('is-active');
    STATE.condition = btn.dataset.condition; // 'all' | 'used' | 'new'
    STATE.fuel = '';                         // clear the other dimension
    cached = null;
    render();
  });

  delegate(tabs, 'click', '[data-fuel]', (e, btn) => {
    deactivateAll();
    btn.classList.add('is-active');
    STATE.fuel = btn.dataset.fuel;           // 'Diesel' | 'PHEV' | 'Hibrid' | 'Benzină'
    STATE.condition = 'all';                 // clear the other dimension
    cached = null;
    render();
  });
}
